import { useMemo } from 'react';
import {
  ALL_OFFERING_CATEGORIES,
  CategoryItem,
  CategoryTrackerData,
  OfferingCategory,
} from './Types';
import { ExperiencePayload, ExperienceResourceArray } from '../Types';
import { programCategoryFor } from '../../../../../Licensing/ProgramCategories';

// Builder v2's licence modal is built around three cards. Group coaching is a
// category of its own in the shared mapping (see the invite flow's licence
// alerts); until this modal is redesigned to carry a fourth card it keeps
// showing group coaching alongside leadership coaching. Tracked separately.
const builderV2Category = (offeringType: string): OfferingCategory => {
  const category = programCategoryFor(offeringType);

  return category === 'group-coaching' ? 'leadership-coaching' : category;
};

interface UseCategoryTrackerProps {
  selectedExperienceIds: number[];
  allExperiences: ExperienceResourceArray;
}

const getCategoryLabel = (category: OfferingCategory): string => {
  switch (category) {
    case 'professor-led':
      return 'Professor-Led';
    case 'on-demand':
      return 'On-Demand';
    case 'leadership-coaching':
      return 'Leadership Coaching';
  }
};

// Hook for category-based tracking (for License Modal)
export const useCategoryTracker = ({
  selectedExperienceIds,
  allExperiences,
}: UseCategoryTrackerProps): CategoryTrackerData => {
  return useMemo(() => {
    // Build a map of experience ID to experience payload
    const experienceMap = new Map<number, ExperiencePayload>();
    allExperiences.forEach((exp) => {
      experienceMap.set(exp.payload.id, exp.payload);
    });

    // Get selected experiences
    const selectedExperiences = selectedExperienceIds
      .map((id) => experienceMap.get(id))
      .filter((exp): exp is ExperiencePayload => exp !== undefined);

    // Group by category
    const categoryData = new Map<
      OfferingCategory,
      { programCount: number; unlicensedCount: number }
    >();

    // Initialize all categories
    ALL_OFFERING_CATEGORIES.forEach((cat) => {
      categoryData.set(cat, { programCount: 0, unlicensedCount: 0 });
    });

    // Count programs per category
    selectedExperiences.forEach((exp) => {
      const category = builderV2Category(exp.offering_type);
      const data = categoryData.get(category)!;
      data.programCount += 1;

      // Check if unlicensed (empty product_license_type_ids)
      if (exp.product_license_type_ids.length === 0) {
        data.unlicensedCount += 1;
      }
    });

    // Build category items (show all categories for complete license overview)
    const categories: CategoryItem[] = ALL_OFFERING_CATEGORIES.map((category) => {
      const data = categoryData.get(category)!;
      return {
        category,
        label: getCategoryLabel(category),
        programCount: data.programCount,
        unlicensedCount: data.unlicensedCount,
        // Licensed if there are programs and none are unlicensed, OR if there are no programs (neutral state)
        isLicensed: data.unlicensedCount === 0,
      };
    });

    const totalUnlicensed = selectedExperiences.filter(
      (exp) => exp.product_license_type_ids.length === 0,
    ).length;

    return {
      categories,
      totalPrograms: selectedExperiences.length,
      totalUnlicensed,
      hasUnlicensedPrograms: totalUnlicensed > 0,
    };
  }, [selectedExperienceIds, allExperiences]);
};
